import { Undo2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Hint } from '../ui/tooltip';
import { laneMeta, diagnosisMeta, priorityMeta } from '../../lib/taxonomy';
import { cn } from '../../lib/utils';

export function LaneChip({ lane, reason, className }) {
  if (!lane) return null;
  const m = laneMeta(lane);
  return (
    <Hint text={reason || m.description}>
      <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border bg-background px-2 py-0.5 text-[11px] font-medium leading-4 text-foreground', className)}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.color }} />
        {m.label}
      </span>
    </Hint>
  );
}

export function LaneDot({ lane, className }) {
  return <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', className)} style={{ backgroundColor: laneMeta(lane).color }} />;
}

export function DiagnosisChip({ diagnosis, reason, className }) {
  if (!diagnosis) return null;
  const m = diagnosisMeta(diagnosis);
  return (
    <Hint text={reason || m.description}>
      <span className={cn('inline-flex whitespace-nowrap rounded-full border border-transparent bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground', className)}>
        {m.label}
      </span>
    </Hint>
  );
}

export function PriorityBadge({ priority, reason, short = false, className }) {
  if (!priority) return null;
  const m = priorityMeta(priority);
  return (
    <Hint text={reason || `${m.label}: ${m.description}`}>
      <span className={cn('inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-4 ring-1 ring-inset', m.badge, className)}>
        {short ? m.short : m.label}
      </span>
    </Hint>
  );
}

export function ReturnBadge({ className }) {
  return (
    <Hint text="Bought in the last 12 months from a supplier we still order from, so it is worth asking them about a return or exchange. This is not a confirmed right to return.">
      <Badge variant="outline" className={className}>
        <Undo2 className="h-3 w-3" /> Ask supplier
      </Badge>
    </Hint>
  );
}

export function ReasonChips({ reasons, className, max }) {
  if (!reasons?.length) return null;
  const list = max ? reasons.slice(0, max) : reasons;
  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {list.map((r) => (
        <span key={r} className="rounded bg-sky-50 px-1.5 py-0.5 text-[10.5px] leading-4 text-sky-800 dark:bg-sky-500/10 dark:text-sky-300">
          {r}
        </span>
      ))}
      {max && reasons.length > max && <span className="px-1 text-[10.5px] text-muted-foreground">+{reasons.length - max}</span>}
    </div>
  );
}

function completenessColor(v) {
  if (v === null || v === undefined) return 'bg-muted-foreground/40';
  if (v < 50) return 'bg-red-500';
  if (v < 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

export function CompletenessBar({ value, className, showLabel = true, showName = false, missing }) {
  const v = value ?? null;
  const bar = (
    <div className={cn('flex items-center gap-1.5', className)}>
      {showName && <span className="shrink-0 text-[11px] text-muted-foreground">Product info</span>}
      <div className="h-1.5 w-full min-w-[32px] overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', completenessColor(v))} style={{ width: `${Math.max(0, Math.min(100, v ?? 0))}%` }} />
      </div>
      {showLabel && <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{v === null ? '—' : `${Math.round(v)}%`}</span>}
    </div>
  );
  const tip = `How complete the product page is (photos, descriptions, specs).${missing?.length ? ` Missing: ${missing.map((m) => m.replace(/_/g, ' ')).join(', ')}` : ''}`;
  return <Hint text={tip}>{bar}</Hint>;
}

export function SimBadge({ value, label = 'look', className }) {
  if (value === null || value === undefined) return null;
  return (
    <Hint text="Visual similarity (0–100)">
      <Badge variant="info" className={cn('tabular-nums', className)}>
        {label} {Math.round(Number(value) * 100)}
      </Badge>
    </Hint>
  );
}
